// Separate review candidates: never replace or relabel the approved V1 studies.
export const FOIL_V2_STYLES = [
  ['classic', 'Classic holo V2', 21, 1, 'Fixed, irregular foil flecks with selective angular reflections.'],
  ['cosmos', 'Cosmos holo V2', 22, 1, 'Fine offset galaxy discs, small stars and occasional curved glints.'],
  ['confetti', 'Confetti holo V2', 23, 1, 'Small rotated fragments with independent facet highlights.'],
  ['pokeball', 'Poké Ball reverse V2', 24, 2, 'Staggered ball outlines with a defined band, button and directional glint.'],
  ['masterball', 'Master Ball reverse V2', 25, 2, 'Staggered ball motifs with an M-shaped accent and directional glint.'],
  ['ex-stars', 'Modern ex star sheen V2', 26, 1, 'Sparse four-point stars over the illustration and narrow outer border.'],
  ['tera', 'Tera ex texture V2', 27, 1, 'Stationary crystalline grooves with light-sensitive facets and a foil border.'],
  ['paldea', 'Paldea-tile reverse V2', 28, 2, 'Repeating geometric tile study with restrained background reflections.'],
  ['gold-etch', 'Mega gold etching V2', 29, 3, 'Fine directional engraving, warm metal and narrow highlights.'],
  ['radiant', 'Radiant diamond V2', 30, 3, 'Fixed diamond lattice with alternating angular reflections.'],
].map(([key, label, pattern, region, description]) => ({ id: `v2-${key}`, label, pattern, region, description }));

export const FOIL_V2_EXAMPLES = {
  'v2-classic': { original: 'classic' },
  'v2-cosmos': { original: 'cosmos' },
  'v2-confetti': { original: 'confetti' },
  'v2-pokeball': { original: 'pokeball' },
  'v2-masterball': { original: 'masterball' },
  'v2-ex-stars': { id: 'sv01-081', finish: 'Miraidon ex · Double rare', window: [.08, .10, .84, .375] },
  'v2-tera': { id: 'sv01-032', finish: 'Arcanine ex · Tera Double rare', window: [.08, .10, .84, .375] },
  'v2-paldea': { original: 'reverse' },
  'v2-gold-etch': { id: 'me01-188', finish: 'Mega Lucario ex · Mega Hyper Rare' },
  'v2-radiant': { original: 'radiant' },
};

export function v2Examples(originals) {
  return Object.fromEntries(Object.entries(FOIL_V2_EXAMPLES).map(([id, example]) => [id, {
    ...(example.original ? originals[example.original] : example),
    study: 'V2 procedural approximation, not a scan or a verified reproduction. Geometric masks cannot isolate the Pokémon or engraved artwork.',
  }]));
}

// V2 has its own shader path. Substrate coordinates never depend on animation:
// only the direction/colour of reflected light changes as each card's clock runs.
export const FOIL_V2_GLSL = `
float stroke(float d, float width){return 1.-smoothstep(width,width+.025,abs(d));}
float segment(vec2 p,vec2 a,vec2 b){vec2 v=b-a;return length(p-a-v*clamp(dot(p-a,v)/dot(v,v),0.,1.));}
vec4 foilV2(vec2 p,float mask,float art){
 vec2 q=p*vec2(1.,1.397)*3.5/patternScale;
 vec2 direction=light*2.-1.;
 float reflection=pow(max(0.,1.-length((p-light)*vec2(.85,1.))),4.);
 float material=0.;
 vec3 tint=rainbow(p.x*.35+p.y*.25+light.x*.25);
 if(pattern<23.5){
  float density=pattern<21.5?24.:(pattern<22.5?14.:27.);
  vec2 cell=floor(q*density), f=fract(q*density)-.5;
  float seed=hash(cell), angle=seed*6.28318;
  f-=vec2(hash(cell+2.),hash(cell+7.))*.34-.17;
  float facing=.20+.80*pow(.5+.5*cos(angle+direction.x*4.+direction.y*3.),3.);
  tint=rainbow(seed*.7+light.x*.3);
  if(pattern<21.5){
   material=step(.48,seed)*(1.-smoothstep(.06,.16,length(f*vec2(1.,1.7))))*facing;
  }else if(pattern<22.5){
   float r=.075+seed*.10,d=length(f);
   float disc=1.-smoothstep(r-.012,r+.012,d);
   float rim=stroke(d-r,.012);
   float star=stroke(min(abs(f.x),abs(f.y)),.008)*(1.-smoothstep(.04,.10,d));
   float arc=stroke(d-.24,.013)*step(.5,cos(atan(f.y,f.x)-angle));
   material=step(.52,seed)*(disc*.28+rim*.4+star*.5)*(.18+facing);
   material+=step(.96,hash(cell+19.))*arc*facing*.65;
  }else{
   vec2 rotated=mat2(cos(angle),-sin(angle),sin(angle),cos(angle))*f;
   float piece=(1.-smoothstep(.09,.12,abs(rotated.x)))*(1.-smoothstep(.045,.07,abs(rotated.y)));
   material=step(.4,seed)*piece*(.12+facing*1.3);
  }
 }else if(pattern<25.5){
  vec2 grid=q*7.;grid.x+=mod(floor(grid.y),2.)*.5;
  vec2 cell=floor(grid),f=fract(grid)-.5;
  float d=length(f),disc=1.-smoothstep(.32,.34,d);
  float ring=stroke(d-.32,.016);
  float button=stroke(d-.072,.012);
  float band=stroke(f.y,.017)*disc*smoothstep(.07,.095,d);
  material=max(ring,max(button,band));
  if(pattern>24.5){
   vec2 a=vec2(-.15,-.12),b=vec2(-.13,-.25),c=vec2(0.,-.15),e=vec2(.13,-.25),g=vec2(.15,-.12);
   float m=min(min(segment(f,a,b),segment(f,b,c)),min(segment(f,c,e),segment(f,e,g)));
   material=max(material,stroke(m,.012));
  }
  material*=.15+pow(.5+.5*cos((f.x-f.y)*5.+direction.x*4.+direction.y*2.),3.);
  tint=rainbow((cell.x+cell.y)*.09+light.x*.25);
 }else if(pattern<27.5){
  // Preserve the text panel; this is only a geometric art+border approximation.
  float border=(1.-step(.035,min(min(p.x,1.-p.x),min(p.y,1.-p.y))))*step(.009,min(min(p.x,1.-p.x),min(p.y,1.-p.y)));
  if(region>.5&&region<1.5)mask=max(art,border);
  if(pattern<26.5){
   vec2 cell=floor(q*11.),f=fract(q*11.)-.5;
   float seed=hash(cell),r=length(f);
   float star=stroke(min(abs(f.x),abs(f.y)),.009)*(1.-smoothstep(.055,.22,r));
   material=step(.65,seed)*star*(.20+.80*pow(.5+.5*cos(seed*6.28+direction.x*5.+direction.y*3.),3.));
  }else{
   vec2 grid=q*13.,cell=floor(grid),f=fract(grid)-.5;
   float facets=stroke(abs(f.x)+abs(f.y)-.43,.018);
   float grooves=pow(.5+.5*sin((q.x+q.y)*590.+sin(q.y*36.)*2.),18.);
   float facing=pow(.5+.5*cos(hash(cell)*6.28+direction.x*4.+direction.y*3.),3.);
   material=(facets*.8+grooves*.28)*(.08+facing);
  }
 }else if(pattern<28.5){
  vec2 grid=q*10.,cell=floor(grid),f=fract(grid)-.5;
  float diamond=stroke(abs(f.x)+abs(f.y)-.42,.018);
  float inner=stroke(abs(f.x)+abs(f.y)-.22,.014);
  material=(diamond*.6+inner*.3)*(.12+pow(.5+.5*cos((cell.x-cell.y)*.7+direction.x*4.),3.));
 }else if(pattern<29.5){
  float groove=pow(.5+.5*sin(q.y*700.+sin(q.x*24.)*5.),20.);
  float cross=pow(.5+.5*sin((q.x-q.y)*480.),24.);
  float facing=pow(.5+.5*cos(sin(q.x*24.)*.7+direction.y*5.+direction.x*2.),3.);
  material=(groove*.8+cross*.2)*(.06+facing);
  tint=mix(vec3(.68,.30,.035),vec3(1.,.85,.35),reflection);
 }else{
  vec2 grid=q*18.,cell=floor(grid);
  float lattice=stroke(abs(fract(grid.x+grid.y)-.5)-.42,.018)+stroke(abs(fract(grid.x-grid.y)-.5)-.42,.018);
  float facing=pow(.5+.5*cos(mod(cell.x+cell.y,2.)*3.14+direction.x*4.+direction.y*2.),3.);
  material=lattice*(.08+facing*.65);
 }
  // A foil substrate still reflects between the narrow motif highlights. Without
  // this restrained moving sheen, sparse stars/flecks disappear on bright scans.
  float film=.025+.10*pow(.5+.5*sin((p.x*.65+p.y*.35-light.x*.8+light.y*.6)*9.),3.);
  return vec4(mix(tint,vec3(1.),reflection*.25),min(.85,mask*strength*(film+reflection*.06+material*(.75+reflection*.65))));
}
`;
